import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  Banknote,
  CalendarDays,
  ChevronLeft,
  Clock,
  Globe,
  LogOut,
  MapPin,
  Navigation,
  Plus,
  RefreshCw,
  Search,
  Users,
  X,
} from 'lucide-react';
import type {
  AttendanceRecord,
  GroupEvent,
  GroupInsights,
  MeetingLocation,
  Participant,
  ParticipantInsights,
} from '../../context/LeadAttendanceContext';
import LanguageSwitcher from './LanguageSwitcher';

interface EventSelectionScreenProps {
  events: GroupEvent[];
  groupName: string;
  leadName: string;
  allowFinance: boolean;
  isLoading: boolean;
  hasMore: boolean;
  search: string;
  statusFilter: string;
  insights: GroupInsights | null;
  isLoadingInsights: boolean;
  participants: Participant[];
  participantsCount: number;
  attendanceHistory: AttendanceRecord[];
  onSelectEvent: (eventId: string) => void;
  onCreateEvent: (payload: {
    title: string;
    startTime: string;
    endTime: string;
    location?: MeetingLocation;
  }) => Promise<any>;
  onLoadMore: () => Promise<void>;
  onFilterChange: (search: string, status?: string) => Promise<void>;
  onRefreshInsights: () => Promise<void>;
  onLoadParticipantInsights: (participantId: string) => Promise<ParticipantInsights>;
  onOpenFinance: () => void;
  onBack: () => void;
  onLogout: () => void;
}

export default function EventSelectionScreen({
  events,
  groupName,
  leadName,
  allowFinance,
  isLoading,
  hasMore,
  search,
  statusFilter,
  insights,
  isLoadingInsights,
  participants,
  participantsCount,
  attendanceHistory,
  onSelectEvent,
  onCreateEvent,
  onLoadMore,
  onFilterChange,
  onRefreshInsights,
  onLoadParticipantInsights,
  onOpenFinance,
  onBack,
  onLogout,
}: EventSelectionScreenProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [title, setTitle] = useState('');
  const [hasEditedTitle, setHasEditedTitle] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [locationMode, setLocationMode] = useState<'physical' | 'online'>('physical');
  const [meetingAddress, setMeetingAddress] = useState('');
  const [meetingUrl, setMeetingUrl] = useState('');
  const [coordinates, setCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(search);
  const [status, setStatus] = useState(statusFilter || '');
  const [isMembersPanelOpen, setIsMembersPanelOpen] = useState(false);
  const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>(null);
  const [participantDetails, setParticipantDetails] = useState<ParticipantInsights | null>(null);
  const [isLoadingParticipant, setIsLoadingParticipant] = useState(false);
  const { t } = useTranslation();

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setSearchTerm(search);
  }, [search]);

  useEffect(() => {
    setStatus(statusFilter || '');
  }, [statusFilter]);

  useEffect(() => {
    if (!isCreating || hasEditedTitle) return;

    const startTime = new Date(date);
    startTime.setHours(19, 0, 0, 0);
    setTitle(`${t('ceremonyAutoTitlePrefix')} - ${startTime.toLocaleString()}`);
  }, [date, hasEditedTitle, isCreating, t]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onFilterChange(searchTerm, status).catch((error) => {
        console.error(error);
      });
    }, 250);

    return () => clearTimeout(timeout);
  }, [onFilterChange, searchTerm, status]);

  useEffect(() => {
    const target = sentinelRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting || isLoading || isLoadingMore || !hasMore) return;

        setIsLoadingMore(true);
        onLoadMore()
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            setIsLoadingMore(false);
          });
      },
      { rootMargin: '280px 0px 280px 0px' },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore, onLoadMore]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const startTime = new Date(date);
      startTime.setHours(19, 0, 0, 0); // default to 7pm
      const endTime = new Date(startTime);
      endTime.setHours(21, 0, 0, 0); // 2 hr default

      const resolvedTitle =
        title.trim() || `${t('ceremonyAutoTitlePrefix')} - ${startTime.toLocaleString()}`;

      const location: MeetingLocation =
        locationMode === 'online'
          ? {
              isVirtual: true,
              meetingUrl: meetingUrl.trim() || undefined,
            }
          : {
              isVirtual: false,
              address: meetingAddress.trim() || undefined,
              coordinates: coordinates || undefined,
            };

      const newEvent = await onCreateEvent({
        title: resolvedTitle,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        location,
      });
      onSelectEvent(newEvent.id);
      setHasEditedTitle(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const topMembers = useMemo(() => {
    if (!insights?.byParticipant?.length) return [];
    return insights.byParticipant.slice(0, 10);
  }, [insights]);

  const visibleCeremoniesCount = events.length;

  const fallbackMembers = useMemo(
    () => participants.slice(0, 20),
    [participants],
  );

  const localAttendanceRate = useMemo(() => {
    if (!attendanceHistory.length) return 0;

    let presentCount = 0;
    let totalCount = 0;

    attendanceHistory.forEach((report) => {
      report.attendances.forEach((entry) => {
        totalCount += 1;
        if (entry.status === 'PRESENT') {
          presentCount += 1;
        }
      });
    });

    return totalCount > 0 ? Math.round((presentCount / totalCount) * 100) : 0;
  }, [attendanceHistory]);

  const presenceRate = useMemo(() => {
    if (insights && insights.totalEvents > 0) {
      return insights.attendanceRate;
    }

    return localAttendanceRate;
  }, [insights, localAttendanceRate]);

  const pendingAttendanceCount = useMemo(() => {
    return events.filter((event) => {
      const status = String(event.status || '');
      if (status === 'CANCELLED') return false;

      return !event.hasAttendanceReport;
    }).length;
  }, [events]);

  const pendingEventIds = useMemo(() => {
    const ids = events
      .filter((event) => {
        const status = String(event.status || '');
        if (status === 'CANCELLED') return false;

        return !event.hasAttendanceReport;
      })
      .map((event) => String(event.id));

    return new Set(ids);
  }, [events]);

  const handleSelectParticipant = async (participantId: string) => {
    setSelectedParticipantId(participantId);
    setIsMembersPanelOpen(true);
    setIsLoadingParticipant(true);

    try {
      const details = await onLoadParticipantInsights(participantId);
      setParticipantDetails(details);
    } catch (error) {
      console.error(error);
      setParticipantDetails(null);
    } finally {
      setIsLoadingParticipant(false);
    }
  };

  return (
    <section className="min-h-[100dvh] bg-slate-50 px-2 py-3 sm:px-3 sm:py-4">
      <div className="mx-auto flex w-full max-w-none flex-col">
        <header className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              title={t('changeLeadCode')}
              aria-label={t('changeLeadCode')}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-500 shadow-sm border border-slate-200"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div>
              <p className="text-sm font-medium text-slate-500">{t('selectCeremony')}</p>
              <h1 className="text-lg font-bold text-slate-900">{groupName}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="light" />
            <button
            onClick={onLogout}
            title={t('logout')}
            aria-label={t('logout')}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-500 shadow-sm border border-slate-200 transition-colors hover:text-red-600"
          >
            <LogOut className="h-5 w-5" />
          </button>
          </div>
        </header>

        {isCreating ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
            <h2 className="text-lg font-bold text-slate-900 mb-4">{t('newCeremony')}</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <label className="block space-y-2">
                <span className="text-sm font-medium text-slate-700">{t('ceremonyTitle')}</span>
                <input
                  type="text"
                  value={title}
                  placeholder={t('ceremonyTitlePlaceholder')}
                  onChange={(e) => {
                    setHasEditedTitle(true);
                    setTitle(e.target.value);
                  }}
                  className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
                />
              </label>
              <label className="block space-y-2">
                <span className="text-sm font-medium text-slate-700">{t('dateLabel')}</span>
                <input
                  type="date"
                  required
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
                />
              </label>

              <section className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center gap-2 text-slate-700">
                  <MapPin className="h-4 w-4 text-brand-600" />
                  <span className="text-sm font-medium">{t('locationLabel')}</span>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setLocationMode('physical')}
                    className={`inline-flex h-9 items-center justify-center rounded-lg border px-3 text-sm font-medium ${
                      locationMode === 'physical'
                        ? 'border-brand-300 bg-brand-50 text-brand-700'
                        : 'border-slate-300 bg-white text-slate-700'
                    }`}
                  >
                    <MapPin className="mr-1.5 h-3.5 w-3.5" />
                    {t('physical')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setLocationMode('online')}
                    className={`inline-flex h-9 items-center justify-center rounded-lg border px-3 text-sm font-medium ${
                      locationMode === 'online'
                        ? 'border-brand-300 bg-brand-50 text-brand-700'
                        : 'border-slate-300 bg-white text-slate-700'
                    }`}
                  >
                    <Globe className="mr-1.5 h-3.5 w-3.5" />
                    {t('online')}
                  </button>
                </div>

                {locationMode === 'physical' ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={meetingAddress}
                      onChange={(event) => setMeetingAddress(event.target.value)}
                      placeholder={t('addressPlaceholder')}
                      className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (!navigator.geolocation) {
                            setLocationError(t('geolocationNotSupported'));
                            return;
                          }

                          setIsLocating(true);
                          setLocationError(null);

                          navigator.geolocation.getCurrentPosition(
                            (position) => {
                              setCoordinates({
                                latitude: position.coords.latitude,
                                longitude: position.coords.longitude,
                              });
                              setIsLocating(false);
                            },
                            (error) => {
                              setLocationError(error.message || t('couldNotGetLocation'));
                              setIsLocating(false);
                            },
                            { enableHighAccuracy: true, timeout: 12000 },
                          );
                        }}
                        disabled={isLocating}
                        className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                      >
                        <Navigation className="h-3.5 w-3.5" />
                        {isLocating ? t('gettingGps') : t('useMyGps')}
                      </button>

                      {coordinates && (
                        <span className="text-xs text-slate-600">
                          {coordinates.latitude.toFixed(6)}, {coordinates.longitude.toFixed(6)}
                        </span>
                      )}
                    </div>

                    {locationError && <p className="text-xs text-rose-600">{locationError}</p>}
                  </div>
                ) : (
                  <input
                    type="url"
                    value={meetingUrl}
                    onChange={(event) => setMeetingUrl(event.target.value)}
                    placeholder="https://meet.google.com/..."
                    className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
                  />
                )}
              </section>
              <div className="pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setHasEditedTitle(false);
                    setIsCreating(false);
                  }}
                  className="h-11 flex-1 rounded-xl border border-slate-300 bg-white font-medium text-slate-700 hover:bg-slate-50"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="h-11 flex-1 rounded-xl bg-brand-600 font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {isSubmitting ? t('saving') : t('create')}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            <div className={`grid grid-cols-2 gap-2 ${allowFinance ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
              <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <p className="text-xs font-medium text-slate-500">{t('statsCardMembers')}</p>
                <p className="mt-1 text-lg font-bold text-slate-900">{participantsCount}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <p className="text-xs font-medium text-slate-500">{t('statsCardCeremonies')}</p>
                <p className="mt-1 text-lg font-bold text-slate-900">{visibleCeremoniesCount}</p>
                <p className="mt-1 text-[11px] text-slate-400">{t('statsCardCurrentList')}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <p className="text-xs font-medium text-slate-500">{t('statsCardPresence')}</p>
                <p className="mt-1 text-lg font-bold text-emerald-700">
                  {isLoadingInsights ? '...' : `${presenceRate}%`}
                </p>
              </div>
              <button
                onClick={() => setIsMembersPanelOpen(true)}
                className="rounded-xl border border-slate-200 bg-white p-3 text-left shadow-sm transition hover:border-brand-300"
              >
                <div className="flex items-center gap-2 text-slate-500">
                  <Users className="h-4 w-4" />
                  <p className="text-xs font-medium">{t('statsCardStats')}</p>
                </div>
                <p className="mt-1 text-sm font-semibold text-brand-700">{t('openPanel')}</p>
              </button>
              {allowFinance && (
                <button
                  onClick={onOpenFinance}
                  className="rounded-xl border border-slate-200 bg-white p-3 text-left shadow-sm transition hover:border-brand-300"
                >
                  <div className="flex items-center gap-2 text-slate-500">
                    <Banknote className="h-4 w-4" />
                    <p className="text-xs font-medium">{t('statsCardFinance')}</p>
                  </div>
                  <p className="mt-1 text-sm font-semibold text-brand-700">{t('openFinance')}</p>
                </button>
              )}
            </div>

            {pendingAttendanceCount > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="inline-flex items-center gap-2 text-sm font-semibold text-amber-800">
                  <AlertTriangle className="h-4 w-4" />
                  {t('pendingAttendanceAlert', { count: pendingAttendanceCount })}
                </p>
              </div>
            )}

            <div className="grid gap-2 sm:grid-cols-5">
              <label className="relative sm:col-span-3">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={t('searchCeremony')}
                  className="h-10 w-full rounded-xl border border-slate-300 bg-white pl-9 pr-3 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
                />
              </label>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                title={t('filterStatus')}
                aria-label={t('filterStatus')}
                className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100 sm:col-span-2"
              >
                <option value="">{t('allStatuses')}</option>
                <option value="SCHEDULED">{t('statusScheduled')}</option>
                <option value="COMPLETED">{t('statusCompleted')}</option>
                <option value="CANCELLED">{t('statusCancelled')}</option>
              </select>
            </div>

            <div className="space-y-3">
            <button
              onClick={() => {
                setHasEditedTitle(false);
                setIsCreating(true);
              }}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white p-4 font-medium text-brand-600 transition-colors hover:border-brand-300 hover:bg-brand-50"
            >
              <Plus className="h-5 w-5" />
              {t('startNewCeremony')}
            </button>

            {isLoading ? (
               <div className="animate-pulse rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="h-5 w-32 rounded bg-slate-200" />
                  <div className="mt-2 h-4 w-48 rounded bg-slate-100" />
               </div>
            ) : events.length === 0 ? (
               <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-card">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                    <CalendarDays className="h-6 w-6 text-slate-400" />
                  </div>
                  <p className="mt-2 font-medium text-slate-900">{t('noCeremoniesFound')}</p>
                  <p className="mt-1 text-sm text-slate-500">{t('startNewCeremonyHint')}</p>
               </div>
            ) : (
               <div className="space-y-3 pb-2 lg:max-h-[calc(100dvh-18rem)] lg:overflow-y-auto lg:pr-1">
                 {events.map((event) => (
                   <button
                     key={event.id}
                     onClick={() => onSelectEvent(event.id)}
                     className={`flex w-full items-center gap-4 rounded-2xl border bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${
                       pendingEventIds.has(String(event.id))
                         ? 'border-amber-300 hover:border-amber-400'
                         : 'border-slate-200 hover:border-brand-200'
                     }`}
                   >
                     <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                       <Clock className="h-6 w-6" />
                     </div>
                     <div className="flex-1 overflow-hidden">
                       <p className="truncate font-semibold text-slate-900">{event.title}</p>
                       <p className="mt-0.5 text-sm text-slate-500">
                         {new Date(event.startTime).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} • {event.status}
                       </p>
                       {pendingEventIds.has(String(event.id)) && (
                         <p className="mt-1 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                           {t('pendingAttendanceShort')}
                         </p>
                       )}
                     </div>
                     <ChevronLeft className="h-5 w-5 rotate-180 text-slate-300" />
                   </button>
                 ))}
                 <div ref={sentinelRef} className="h-4 w-full" />
                 {isLoadingMore && (
                   <div className="rounded-xl border border-slate-200 bg-white p-3 text-center text-sm text-slate-500">
                     {t('loadingMore')}
                   </div>
                 )}
               </div>
            )}

            </div>

            {isMembersPanelOpen && (
              <div className="fixed inset-0 z-40 bg-slate-950/35 backdrop-blur-sm">
                <div className="absolute inset-x-0 bottom-0 top-12 rounded-t-3xl bg-white shadow-2xl sm:left-auto sm:right-4 sm:top-4 sm:h-[calc(100dvh-2rem)] sm:w-[420px] sm:rounded-3xl">
                  <div className="flex h-full flex-col">
                    <div className="flex items-start justify-between border-b border-slate-200 p-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-brand-600" />
                          <h3 className="text-sm font-semibold text-slate-900">{t('membersPanelTitle')}</h3>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">{t('noRealtime')}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={onRefreshInsights}
                          className="inline-flex h-9 items-center gap-1 rounded-lg border border-slate-200 px-3 text-xs font-medium text-slate-700 hover:border-brand-200 hover:text-brand-700"
                        >
                          <RefreshCw className={`h-3.5 w-3.5 ${isLoadingInsights ? 'animate-spin' : ''}`} />
                          {t('refresh')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsMembersPanelOpen(false)}
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700"
                          aria-label={t('closeMembersPanel')}
                          title={t('closeMembersPanel')}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4">
                      <p className="mb-3 text-sm text-slate-500">
                        {t('membersPanelDescription')}
                      </p>

                      <div className="sticky top-0 z-10 mb-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                        {isLoadingParticipant ? (
                          <p className="text-sm text-slate-500">{t('loadingMemberDetails')}</p>
                        ) : participantDetails ? (
                          <div className="space-y-3">
                            <div>
                              <p className="text-xs text-slate-500">{t('selectedMember')}</p>
                              <p className="text-base font-bold text-slate-900">{participantDetails.participant.name}</p>
                              {participantDetails.participant.email && (
                                <p className="text-xs text-slate-500">{participantDetails.participant.email}</p>
                              )}
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                              <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                                <p className="text-[11px] text-slate-500">{t('rateLabel')}</p>
                                <p className="text-sm font-bold text-emerald-700">{participantDetails.summary.attendanceRate}%</p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                                <p className="text-[11px] text-slate-500">{t('consistencyLabel')}</p>
                                <p className="text-sm font-bold text-slate-800">{participantDetails.summary.consistencyScore}</p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                                <p className="text-[11px] text-slate-500">{t('presentLabel')}</p>
                                <p className="text-sm font-bold text-slate-800">{participantDetails.summary.present}</p>
                              </div>
                              <div className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                                <p className="text-[11px] text-slate-500">{t('absencesLabel')}</p>
                                <p className="text-sm font-bold text-slate-800">{participantDetails.summary.absent}</p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="rounded-lg border border-dashed border-slate-300 p-3 text-xs text-slate-500">
                            {t('selectMemberForDetails')}
                          </div>
                        )}
                      </div>

                      {topMembers.length > 0 ? (
                        <div className="space-y-2">
                          {topMembers.map((member) => (
                            <button
                              key={member.participantId}
                              onClick={() => handleSelectParticipant(member.participantId)}
                              className={`w-full rounded-xl border p-3 text-left transition ${
                                selectedParticipantId === member.participantId
                                  ? 'border-brand-300 bg-brand-50'
                                  : 'border-slate-200 hover:border-brand-200'
                              }`}
                            >
                              <p className="truncate text-sm font-semibold text-slate-900">{member.name}</p>
                              <p className="mt-1 text-xs text-slate-500">
                                {t('attendanceRateLabel')}: <span className="font-semibold text-slate-700">{member.attendanceRate}%</span>
                                {' • '}P: {member.present} {' • '}A: {member.absent}
                              </p>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('groupMembers')}</p>
                            <p className="mt-1 text-sm text-slate-600">{t('noEnoughAttendance')}</p>
                          </div>
                          {fallbackMembers.length === 0 ? (
                            <div className="rounded-lg border border-dashed border-slate-300 p-3 text-xs text-slate-500">
                              {t('noMembersFound')}
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {fallbackMembers.map((member) => (
                                <button
                                  key={member.id}
                                  onClick={() => handleSelectParticipant(member.id)}
                                  className={`w-full rounded-xl border p-3 text-left transition ${
                                    selectedParticipantId === member.id
                                      ? 'border-brand-300 bg-brand-50'
                                      : 'border-slate-200 hover:border-brand-200'
                                  }`}
                                >
                                  <p className="truncate text-sm font-semibold text-slate-900">{member.name}</p>
                                  <p className="mt-1 text-xs text-slate-500">{member.phone || t('noPhoneRegistered')}</p>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {participantDetails && (
                        <div className="mt-4">
                          <p className="mb-2 text-xs font-semibold text-slate-600">{t('recentCeremonies')}</p>
                          <div className="space-y-2">
                            {participantDetails.recentAttendances.slice(0, 5).map((item) => (
                              <div key={`${item.eventId}-${item.markedAt || item.eventDate || item.status}`} className="rounded-lg border border-slate-200 p-2">
                                <p className="truncate text-xs font-semibold text-slate-800">{item.eventTitle}</p>
                                <p className="mt-1 text-[11px] text-slate-500">
                                  {item.eventDate ? new Date(item.eventDate).toLocaleDateString() : '-'}
                                  {' • '}
                                  <span className="font-semibold text-slate-700">{item.status}</span>
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
